import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deviceType } from 'react-device-detect';

import AppLoader from 'components/v2/appLoader';

import api, { MeResponse } from 'api';
import setUpInterceptor from 'api/interceptors';
import { BackendEventProps } from 'api/models/payload/EventsPayload';
import { logout as logoutThunk } from 'redux/auth/auth-middleware';
import { loadPermissions } from 'redux/permissions/permissions-middleware';
import { AppDispatch, RootState } from 'redux/store';
import { loadMe } from 'redux/user/user-middleware';
import { fetchProfilePictureUrl, profilePictureKeys } from 'hooks/useMyAccount';
import { queryClient } from 'queryClient';
import {
    broadcastAuthLogout,
    isAuthenticationRoute,
    subscribeToAuthLogout,
    type AuthLogoutReason,
    WRONG_PORTAL_LOGOUT_REASON,
} from 'utils/authSession';
import { getLocation } from 'utils/location';
import storage from 'utils/storageController';

interface User extends MeResponse {
    email: string;
    file_number: string;
    firstname: string;
    id: number;
    lastname: string;
    middlename: string;
    role: string;
}

interface IUserContext {
    user: User | null;
    setUser: (state: User) => void;
    logout: () => void;
    /** True until the initial session check (logged-in or not) has finished */
    isInitializing: boolean;
}

const initUserContext: IUserContext = {
    user: null,
    setUser: () => null,
    logout: () => null,
    isInitializing: true,
};

const UserContext = createContext<IUserContext>(initUserContext);

export default function UserProvider({ children }: { children: JSX.Element }) {
    const [user, setUser] = useState<User | null>(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const dispatch = useDispatch<AppDispatch>();
    const store = useStore<RootState>();
    const navigate = useNavigate();

    const country: string = getLocation().userCountry;
    const [queryParameters] = useSearchParams();

    const terminateSession = useCallback(
        (reason: AuthLogoutReason = 'session-expired', broadcast = true) => {
            void dispatch(logoutThunk());
            setUser(null);

            if (broadcast) {
                broadcastAuthLogout(reason);
            }

            if (isAuthenticationRoute(window.location.pathname)) {
                return;
            }

            const loginPath =
                reason === WRONG_PORTAL_LOGOUT_REASON ? '/login?reason=wrong-portal' : '/login';
            navigate(loginPath, { replace: true });
        },
        [dispatch, navigate]
    );

    // Install axios interceptors before any bootstrap API call.
    // Previously this lived only in App, which sits under this provider —
    // if we gate children behind the loader, App never mounts in time for
    // session refresh on /me.
    setUpInterceptor(store, terminateSession);

    useEffect(
        () => subscribeToAuthLogout((reason) => terminateSession(reason, false)),
        [terminateSession]
    );

    useEffect(() => {
        let cancelled = false;

        const trackUserProps = async () => {
            const url_params = window.document.referrer
                ? new URL(window.document.referrer).searchParams
                : null;
            const event_properties: BackendEventProps = {
                country: country,
                referrer: window.document.referrer,
                device_type: deviceType,
                utm_source:
                    queryParameters.get('utm_source') || url_params?.get('utm_source') || '',
                utm_medium:
                    queryParameters.get('utm_medium') || url_params?.get('utm_medium') || '',
                utm_campaign:
                    queryParameters.get('utm_campaign') || url_params?.get('utm_campaign') || '',
                utm_content:
                    queryParameters.get('utm_content') || url_params?.get('utm_content') || '',
                user_agent: window.navigator.userAgent,
                anonymous_id: localStorage.ajs_anonymous_id?.replaceAll('"', ''),
            };
            try {
                const res = await api.events.user_props_put(event_properties);
                if (res.status !== 200 && res.status !== 201) {
                    console.log(res.statusText);
                }
            } catch {
                // Non-blocking analytics — ignore failures
            }
        };

        const bootstrapAuth = async () => {
            try {
                if (!storage.isAuthenticated()) {
                    if (!cancelled) {
                        // Drop any stale role left without a refresh token so
                        // ProtectedRoute does not treat the session as valid.
                        storage.removeUserRole();
                        setUser(null);
                    }
                    return;
                }

                // Single me() for context + Redux (replaces dual parallel me fetches)
                const meResult = await dispatch(loadMe());
                if (cancelled) return;

                if (loadMe.fulfilled.match(meResult)) {
                    const meData = meResult.payload.data;
                    storage.setUserRole(meData.role);
                    setUser(meData);

                    // Prefetch profile picture into react-query (shared with useProfilePicture)
                    await queryClient.prefetchQuery({
                        queryKey: profilePictureKeys.all,
                        queryFn: fetchProfilePictureUrl,
                    });
                    if (cancelled) return;

                    if (meData.id) {
                        await dispatch(loadPermissions(meData.id));
                    }
                    if (cancelled) return;

                    // Fire-and-forget campaign props (do not block UI readiness)
                    void trackUserProps();
                } else {
                    storage.logout();
                    setUser(null);
                }
            } catch {
                if (!cancelled) {
                    storage.logout();
                    setUser(null);
                }
            } finally {
                if (!cancelled) {
                    setIsInitializing(false);
                }
            }
        };

        void bootstrapAuth();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for session bootstrap
    }, []);

    // Replace company entitlements when the user returns to the tab — an admin
    // can toggle flags without requiring a new login.
    useEffect(() => {
        let lastFetchedAt = 0;

        const refetchEnabledFeatures = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            if (!storage.isAuthenticated()) {
                return;
            }
            const now = Date.now();
            if (now - lastFetchedAt < 2000) {
                return;
            }
            lastFetchedAt = now;
            void dispatch(loadMe());
        };

        document.addEventListener('visibilitychange', refetchEnabledFeatures);
        return () => {
            document.removeEventListener('visibilitychange', refetchEnabledFeatures);
        };
    }, [dispatch]);

    const logout = () => {
        storage.logout();
        queryClient.removeQueries({ queryKey: profilePictureKeys.all });
        setUser(null);
    };

    if (isInitializing) {
        return <AppLoader />;
    }

    return (
        <UserContext.Provider value={{ user, setUser, logout, isInitializing }}>
            {children}
        </UserContext.Provider>
    );
}

export function useUser() {
    return useContext(UserContext);
}
